import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/api";
import { useAuth, isAdmin } from "@/context/AuthContext";
import { CANTIERE } from "@/constants/testIds";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, LineChart, Line, CartesianGrid } from "recharts";
import { HardHat, MapPin, Calendar, User, FileDown, ArrowLeft, TrendingUp, TrendingDown, ClipboardList, Receipt, Fingerprint, Plus } from "lucide-react";
import { DocumentiTab, FotografieTab, ComunicazioniTab } from "@/pages/cantiere/CantiereTabs";
import NoteCantiereTab from "@/pages/cantiere/NoteCantiereTab";
import SubappaltiTab from "@/pages/cantiere/SubappaltiTab";
import RichiesteSubappaltatoriTab from "@/pages/cantiere/RichiesteSubappaltatoriTab";
import FatturatoEffettivoTab from "@/pages/cantiere/FatturatoEffettivoTab";
import IncassiCantiere from "@/pages/cantiere/IncassiCantiere";
import LavorazioniExtraTab from "@/pages/cantiere/LavorazioniExtraTab";
import RichiesteMaterialiTab from "@/pages/cantiere/RichiesteMaterialiTab";
import GanttView from "@/pages/cantiere/CantiereGanttView";
import NotificheLogTab from "@/pages/cantiere/NotificheLogTab";
import BollaForm from "@/pages/bolle/BollaForm";
import { codiceBolla } from "@/lib/codici";
import { useLiveUpdates } from "@/hooks/useLiveUpdates";

const eur = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);
const eur2 = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(n || 0);
const num = (n) => new Intl.NumberFormat("it-IT").format(n || 0);

const COLORS = ["#ea580c", "#0f172a", "#2563eb", "#16a34a", "#eab308", "#7c3aed", "#dc2626"];

const TABS_ADMIN = [
  { key: "info", label: "Info", testId: CANTIERE.tabInfo },
  { key: "economia", label: "Economia", testId: CANTIERE.tabEconomia },
  { key: "crono", label: "Cronoprogramma", testId: CANTIERE.tabCrono },
  { key: "rapportini", label: "Rapportini", testId: CANTIERE.tabRapportini },
  { key: "bolle", label: "Bolle", testId: CANTIERE.tabBolle },
  { key: "richieste-materiali", label: "Richieste fornitori", testId: "cantiere-tab-richieste-materiali" },
  { key: "richieste-subappaltatori", label: "Richieste a subappaltatore", testId: "cantiere-tab-richieste-subappaltatori" },
  { key: "subappalti", label: "Subappalti", testId: "cantiere-tab-subappalti" },
  { key: "fatture", label: "Fatturato effettivo", testId: "cantiere-tab-fatture" },
  { key: "presenze", label: "Presenze", testId: CANTIERE.tabPresenze },
  { key: "documenti", label: "Documenti", testId: "cantiere-tab-documenti" },
  { key: "fotografie", label: "Fotografie", testId: "cantiere-tab-fotografie" },
  { key: "comunicazioni", label: "Comunicazioni", testId: "cantiere-tab-comunicazioni" },
  { key: "note", label: "Note", testId: "cantiere-tab-note" },
  { key: "notifiche-log", label: "Storico notifiche", testId: "cantiere-tab-notifiche-log" },
];
const TABS_WORKER_BASE = [
  { key: "info", label: "Info", testId: CANTIERE.tabInfo },
  { key: "crono", label: "Cronoprogramma", testId: CANTIERE.tabCrono },
  { key: "rapportini", label: "Rapportini", testId: CANTIERE.tabRapportini },
  { key: "bolle", label: "Bolle", testId: CANTIERE.tabBolle },
  { key: "presenze", label: "Presenze", testId: CANTIERE.tabPresenze },
  { key: "documenti", label: "Documenti", testId: "cantiere-tab-documenti" },
  { key: "fotografie", label: "Fotografie", testId: "cantiere-tab-fotografie" },
  { key: "comunicazioni", label: "Comunicazioni", testId: "cantiere-tab-comunicazioni" },
  { key: "note", label: "Note", testId: "cantiere-tab-note" },
];

export default function CantiereDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const admin = isAdmin(user);
  // Supporta ?tab=crono nel link (usato dalla dashboard Programma Lavori)
  const searchParams = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : null;
  const initialTab = searchParams?.get("tab") || (admin ? "economia" : "info");
  const [tab, setTab] = useState(initialTab);
  const qc = useQueryClient();

  // ---- Nuova bolla dal dettaglio cantiere / manutenzione ----
  const [bollaOpen, setBollaOpen] = useState(false);
  const [bollaEditingId, setBollaEditingId] = useState(null);
  const emptyBolla = () => ({
    numero_bolla: "",
    data: new Date().toISOString().slice(0, 10),
    fornitore: "",
    cantiere_id: id,
    materiale: "",
    quantita: "",
    unita_misura: "pz",
    prezzo_unitario: "",
    aliquota_iva: "22",
    categoria: "Materiali",
    numero_ordine: "",
    note: "",
    allegati: [],
    fornitore_partita_iva: "",
    fornitore_indirizzo: "",
  });
  const [bollaForm, setBollaForm] = useState(emptyBolla);
  const handleBollaFiles = async (files) => {
    for (const file of Array.from(files || [])) {
      if (file.size > 6 * 1024 * 1024) { toast.error(`"${file.name}" troppo grande (max 6 MB)`); continue; }
      const b64 = await new Promise((res, rej) => {
        const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file);
      }).catch(() => null);
      if (b64) setBollaForm((f) => ({ ...f, allegati: [...f.allegati, b64] }));
    }
  };
  const removeBollaAllegato = (idx) =>
    setBollaForm((f) => ({ ...f, allegati: f.allegati.filter((_, i) => i !== idx) }));

  const saveBolla = useMutation({
    mutationFn: async (payload) => {
      // best-effort upsert anagrafica fornitore
      if (payload.fornitore && (payload.fornitore_partita_iva || payload.fornitore_indirizzo)) {
        api.post("/entita/fornitori", {
          ragione_sociale: payload.fornitore,
          partita_iva: payload.fornitore_partita_iva || "",
          indirizzo: payload.fornitore_indirizzo || "",
          categoria: payload.categoria || "Materiali",
        }).catch(() => {});
      }
      const num = (v) => {
        if (v === null || v === undefined || v === "") return 0;
        if (typeof v === "number") return v;
        const n = parseFloat(String(v).trim().replace(",", "."));
        return isNaN(n) ? 0 : n;
      };
      const { fornitore_partita_iva, fornitore_indirizzo, ...rest } = payload;
      const clean = {
        ...rest,
        quantita: num(rest.quantita),
        prezzo_unitario: num(rest.prezzo_unitario),
        aliquota_iva: num(rest.aliquota_iva),
      };
      if (bollaEditingId) return api.patch(`/bolle/${bollaEditingId}`, clean).then((r) => r.data);
      return api.post("/bolle", clean).then((r) => r.data);
    },
    onSuccess: () => {
      toast.success(bollaEditingId ? "Bolla aggiornata" : "Bolla registrata");
      qc.invalidateQueries({ queryKey: ["bolle", id] });
      qc.invalidateQueries({ queryKey: ["bolle-all"] });
      qc.invalidateQueries({ queryKey: ["cantiere-econ", id] });
      setBollaOpen(false);
      setBollaEditingId(null);
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });

  const openNewBolla = () => {
    setBollaEditingId(null);
    setBollaForm(emptyBolla());
    setBollaOpen(true);
  };
  const bollaAnteprima = (() => {
    const num = (v) => {
      if (v === null || v === undefined || v === "") return 0;
      if (typeof v === "number") return v;
      const n = parseFloat(String(v).trim().replace(",", "."));
      return isNaN(n) ? 0 : n;
    };
    const imp = num(bollaForm.quantita) * num(bollaForm.prezzo_unitario);
    const iva = imp * num(bollaForm.aliquota_iva) / 100;
    return { imponibile: imp.toFixed(2), iva: iva.toFixed(2), totale: (imp + iva).toFixed(2) };
  })();

  const { data: cantiere } = useQuery({
    queryKey: ["cantiere", id],
    queryFn: () => api.get(`/cantieri/${id}`).then((r) => r.data),
  });

  const { data: econData } = useQuery({
    queryKey: ["cantiere-econ", id],
    queryFn: () => api.get(`/cantieri/${id}/economics`).then((r) => r.data),
    enabled: admin,
  });

  const { data: attivita = [] } = useQuery({
    queryKey: ["crono", id],
    queryFn: () => api.get(`/cronoprogramma?cantiere_id=${id}`).then((r) => r.data),
  });

  const { data: andamento } = useQuery({
    queryKey: ["crono-andamento", id],
    queryFn: () => api.get(`/cronoprogramma/andamento/${id}`).then((r) => r.data),
  });

  const { data: rapportini = [] } = useQuery({
    queryKey: ["rapportini", id],
    queryFn: () => api.get(`/rapportini?cantiere_id=${id}`).then((r) => r.data),
  });

  const { data: bolle = [] } = useQuery({
    queryKey: ["bolle", id],
    queryFn: () => api.get(`/bolle?cantiere_id=${id}`).then((r) => r.data),
  });

  const { data: presenze = [] } = useQuery({
    queryKey: ["presenze", id],
    queryFn: () => api.get(`/timbrature?cantiere_id=${id}`).then((r) => r.data),
  });

  if (!cantiere) return <div>Caricamento…</div>;
  const econ = econData?.economics;

  const tabs = admin
    ? TABS_ADMIN
    : (user?.puo_richieste_materiali
        ? [
            ...TABS_WORKER_BASE.slice(0, 4),
            { key: "richieste-materiali", label: "Richieste fornitori", testId: "cantiere-tab-richieste-materiali" },
            ...TABS_WORKER_BASE.slice(4),
          ]
        : TABS_WORKER_BASE);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  return (
    <div className="space-y-6">
      <Link to="/cantieri" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900">
        <ArrowLeft className="w-4 h-4" /> Cantieri
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="overline">{cantiere.commessa}</div>
          <h1 className="font-display text-3xl xl:text-4xl font-black">{cantiere.nome}</h1>
          <div className="flex flex-wrap gap-4 mt-2 text-sm text-slate-600">
            <div className="flex items-center gap-1"><User className="w-4 h-4" /> {cantiere.cliente}</div>
            <div className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {cantiere.indirizzo}</div>
            <div className="flex items-center gap-1"><Calendar className="w-4 h-4" /> {cantiere.data_inizio} → {cantiere.data_fine_prevista}</div>
          </div>
        </div>
        {admin && (
          <div className="flex gap-2 flex-wrap">
            <a href={`${backendUrl}/api/export/cantiere/${id}/excel`} data-testid={CANTIERE.exportExcel} className="flex items-center gap-1 px-3 py-2 text-sm border border-slate-300 hover:bg-slate-50 rounded-sm">
              <FileDown className="w-4 h-4" /> Excel
            </a>
            <a href={`${backendUrl}/api/export/cantiere/${id}/pdf`} data-testid={CANTIERE.exportPdf} className="flex items-center gap-1 px-3 py-2 text-sm border border-slate-300 hover:bg-slate-50 rounded-sm">
              <FileDown className="w-4 h-4" /> PDF
            </a>
            <a
              href={`${backendUrl}/api/cantieri/${id}/report-completo/pdf`}
              target="_blank" rel="noreferrer"
              data-testid="btn-riepilogo-cantiere-pdf"
              title="PDF di riepilogo con tutte le richieste materiali e le bolle — per la chiusura cantiere"
              className="flex items-center gap-1 px-3 py-2 text-sm bg-slate-900 hover:bg-slate-800 text-white font-semibold rounded-sm"
            >
              <FileDown className="w-4 h-4" /> Riepilogo completo
            </a>
          </div>
        )}
      </div>

      {/* Andamento cantiere banner */}
      {andamento && (
        <div className="bg-white border border-slate-200 p-4 flex flex-wrap gap-4 items-center justify-between">
          <div>
            <div className="overline">Andamento cantiere</div>
            <div className={`font-display text-lg font-bold ${
              andamento.stato === "in_anticipo" ? "text-emerald-700" :
              andamento.stato === "in_linea" ? "text-slate-900" :
              andamento.stato === "leggermente_in_ritardo" ? "text-yellow-700" : "text-red-700"
            }`}>
              {andamento.stato === "nessuna_attivita" ? "Nessuna attività pianificata" :
               andamento.stato === "in_anticipo" ? "In anticipo" :
               andamento.stato === "in_linea" ? "In linea" :
               andamento.stato === "leggermente_in_ritardo" ? "Leggermente in ritardo" : "In forte ritardo"}
            </div>
          </div>
          <div className="flex gap-6 text-sm">
            <div>
              <div className="overline text-[9px]">Previsto</div>
              <div className="font-mono font-bold">{andamento.avanzamento_previsto}%</div>
            </div>
            <div>
              <div className="overline text-[9px]">Effettivo</div>
              <div className="font-mono font-bold">{andamento.avanzamento_effettivo}%</div>
            </div>
            <div>
              <div className="overline text-[9px]">Differenza</div>
              <div className={`font-mono font-bold ${andamento.differenza >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                {andamento.differenza >= 0 ? "+" : ""}{andamento.differenza}%
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-slate-200 flex overflow-x-auto">
        {tabs.map((t) => (
          <button
            key={t.key}
            data-testid={t.testId}
            onClick={() => setTab(t.key)}
            className={`px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
              tab === t.key ? "border-orange-600 text-slate-900" : "border-transparent text-slate-600 hover:text-slate-900"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "info" && (
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-white border border-slate-200 p-5">
            <div className="overline mb-3">Informazioni generali</div>
            <dl className="text-sm space-y-2">
              <Row label="Referente" value={cantiere.referente} />
              <Row label="Responsabile" value={cantiere.responsabile} />
              <Row label="Stato" value={cantiere.stato} />
              <Row label="Data inizio" value={cantiere.data_inizio} />
              <Row label="Data fine prevista" value={cantiere.data_fine_prevista} />
              <Row label="Indirizzo" value={cantiere.indirizzo} />
              <Row label="Coordinate" value={cantiere.lat && cantiere.lng ? `${cantiere.lat}, ${cantiere.lng}` : "-"} />
              <Row label="Raggio autorizzato" value={`${cantiere.raggio_metri} m`} />
            </dl>
          </div>
          <div className="bg-white border border-slate-200 p-5">
            <div className="overline mb-3">Lavoratori assegnati</div>
            <div className="flex flex-wrap gap-2">
              {(cantiere.lavoratori_assegnati || []).length === 0 && <div className="text-slate-400 text-sm">Nessuno</div>}
              {(cantiere.lavoratori_assegnati || []).map((l) => (
                <span key={l} className="text-xs px-2 py-1 bg-slate-100 border border-slate-200 rounded-sm">{l}</span>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === "economia" && admin && econ && (
        <div className="space-y-6">
          {/* Ricavi & fatturazione */}
          <div className="grid md:grid-cols-4 gap-4">
            <Card label="Contratto" value={eur(econ.importo_contratto)} />
            <Card label="Extra" value={eur(econ.importo_extra)} />
            <Card label="Totale previsto" value={eur(econ.importo_totale_previsto)} highlight />
            <Card label="Fatturato" value={eur(econ.importo_fatturato)} sub={`da fatturare: ${eur(econ.importo_da_fatturare)}`} />
            <Card label="Incassato" value={eur(econ.importo_incassato)} sub={`da incassare: ${eur(econ.importo_da_incassare)}`} />
            <Card label="Avanz. economico" value={`${econ.percentuale_avanzamento_economico}%`} />
            <Card label="Avanz. lavori" value={`${econ.percentuale_avanzamento_lavori}%`} />
            <Card label="Guadagno netto" value={econ.incidenza_disponibile ? eur(econ.guadagno_netto) : "Da configurare"} highlight={econ.guadagno_netto>=0} negative={econ.guadagno_netto<0} />
          </div>

          <IncassiCantiere cantiereId={id} />
          <LavorazioniExtraTab cantiereId={id} />

          {/* IVA cliente Privato: override per cantiere (22% default, 10% opzionale) */}
          {econData?.cantiere?.tipo_cliente === "privato" && (
            <div className="bg-white border-2 border-amber-300 shadow-brutal p-4" data-testid="econ-iva-privato-toggle">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <div className="overline text-amber-800">IVA cliente Privato</div>
                  <div className="text-sm text-slate-700 mt-1">
                    Aliquota IVA applicata al ricavo previsionale dei rapportini di questo cantiere.
                    <span className="block text-xs text-slate-500 mt-1">
                      Cambia solo per questo cantiere. Altri tipi cliente (Condominio, Azienda) non sono influenzati.
                    </span>
                  </div>
                </div>
                <div className="flex gap-2" role="group" aria-label="Scelta aliquota IVA Privato">
                  {[22, 10].map((pct) => {
                    const current = Number(econData.cantiere.aliquota_iva_privato ?? 22) === pct;
                    return (
                      <button
                        key={pct}
                        type="button"
                        data-testid={`btn-iva-privato-${pct}`}
                        disabled={current}
                        onClick={() => {
                          if (!window.confirm(`Applicare IVA ${pct}% ai ricavi previsionali del cliente Privato per questo cantiere?\n\nRicalcolerà automaticamente importo IVA, totali e riepiloghi economici.`)) return;
                          api.patch(`/cantieri/${id}`, {
                            ...econData.cantiere,
                            aliquota_iva_privato: pct,
                          }).then(() => {
                            toast.success(`IVA Privato aggiornata a ${pct}%`);
                            qc.invalidateQueries({ queryKey: ["cantiere-econ", id] });
                            qc.invalidateQueries({ queryKey: ["cantiere", id] });
                            qc.invalidateQueries({ queryKey: ["iva"] });
                          }).catch((e) => toast.error(e.response?.data?.detail || "Errore aggiornamento IVA"));
                        }}
                        className={`px-4 py-2 text-sm font-bold border-2 rounded-sm transition ${
                          current
                            ? "bg-amber-500 border-amber-700 text-white cursor-default shadow-brutal"
                            : "bg-white border-slate-300 text-slate-700 hover:border-amber-500 hover:bg-amber-50"
                        }`}
                      >
                        IVA {pct}%
                        {current && <span className="ml-2 text-[10px] font-normal opacity-80">attiva</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Calcolo guadagno (formula utente) */}
          <div className="bg-white border-2 border-slate-900 shadow-brutal p-5" data-testid="economia-calcolo-guadagno">
            <div className="overline mb-3">Calcolo guadagno · aggiornato in tempo reale</div>
            <div className="grid md:grid-cols-2 gap-6">
              <table className="w-full text-sm">
                <tbody>
                  <tr>
                    <td className="py-2 text-slate-700">Contratto</td>
                    <td className="py-2 text-right font-mono">{eur2(econ.importo_contratto)}</td>
                  </tr>
                  <tr>
                    <td className="py-2 text-slate-700">Extra</td>
                    <td className="py-2 text-right font-mono">+ {eur2(econ.importo_extra)}</td>
                  </tr>
                  <tr className="border-t border-slate-300">
                    <td className="py-2 font-bold">Ricavi totali</td>
                    <td className="py-2 text-right font-mono font-bold text-emerald-700" data-testid="econ-ricavi">
                      {eur2(econ.ricavi)}
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={2} className="pt-4 pb-1 text-[10px] font-bold uppercase tracking-widest text-rose-700">
                      Costi da sottrarre
                    </td>
                  </tr>
                  <tr>
                    <td className="py-2 text-slate-700">Ore lavorate (manodopera)</td>
                    <td className="py-2 text-right font-mono text-rose-700" data-testid="econ-manodopera">
                      − {eur2(econ.costo_manodopera)}
                    </td>
                  </tr>
                  <tr>
                    <td className="py-2 text-slate-700">Materiali (bolle)</td>
                    <td className="py-2 text-right font-mono text-rose-700" data-testid="econ-materiali">
                      − {eur2(econ.costo_materiali)}
                    </td>
                  </tr>
                  <tr>
                    <td className="py-2 text-slate-700">Altri costi diretti (trasferte, sicurezza, tecnici, subappalti, mezzi, noleggi, trasporti)</td>
                    <td className="py-2 text-right font-mono text-rose-700">
                      − {eur2(
                        (Number(econ.costi_diretti) || 0)
                        - (Number(econ.costo_manodopera) || 0)
                        - (Number(econ.costo_materiali) || 0)
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="py-2 text-slate-700">Costi gestione aziendale ({econ.incidenza_disponibile ? `${Number(econ.incidenza_costi_gestione_pct).toFixed(2)}%` : "Da configurare"})</td>
                    <td className="py-2 text-right font-mono text-rose-700" data-testid="econ-17pct">
                      {econ.incidenza_disponibile ? `− ${eur2(econ.costo_gestione_cantiere)}` : "Da configurare"}
                    </td>
                  </tr>
                  <tr className="border-t-2 border-slate-900">
                    <td className="py-3 font-display font-bold text-lg">Guadagno lordo</td>
                    <td className={`py-3 text-right font-mono font-black text-lg ${econ.guadagno_lordo >= 0 ? "text-emerald-700" : "text-rose-700"}`}
                        data-testid="econ-guadagno-lordo">
                      {econ.incidenza_disponibile ? eur2(econ.guadagno_lordo) : "Da configurare"}
                    </td>
                  </tr>
                  <tr>
                    <td className="py-2 text-slate-700">Tassazione 27% sul lordo</td>
                    <td className="py-2 text-right font-mono text-rose-700" data-testid="econ-27pct">
                      {econ.incidenza_disponibile ? `− ${eur2(econ.tasse_27_pct)}` : "Da configurare"}
                    </td>
                  </tr>
                  <tr className="border-t-2 border-emerald-700 bg-emerald-50">
                    <td className="py-3 font-display font-black text-lg">GUADAGNO NETTO</td>
                    <td className={`py-3 text-right font-mono font-black text-xl ${econ.guadagno_netto >= 0 ? "text-emerald-800" : "text-rose-800"}`}
                        data-testid="econ-guadagno-netto">
                      {econ.incidenza_disponibile ? eur2(econ.guadagno_netto) : "Da configurare"}
                    </td>
                  </tr>
                </tbody>
              </table>
              <div className="bg-slate-50 p-4 rounded-sm text-sm">
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">Formula applicata</div>
                <div className="space-y-2 font-mono text-xs text-slate-700 leading-relaxed">
                  <div><b>Ricavi</b> = Contratto + Extra</div>
                  <div><b>Costi</b> = Manodopera + Trasferte + Materiali senza IVA + Sicurezza + Tecnici + Subappalti + Mezzi + Noleggi + Trasporti + (Incidenza costi di gestione aziendale × Ricavi)</div>
                  <div className="text-emerald-800"><b>Guadagno lordo</b> = Ricavi − Costi</div>
                  <div className="text-emerald-900"><b>Guadagno netto</b> = Guadagno lordo × (1 − 27%)</div>
                </div>
                <div className="mt-4 pt-3 border-t border-slate-200 text-xs text-slate-600">
                  Aggiornato automaticamente quando modifichi <b>contratto</b>, <b>extra</b>, <b>ore lavorate</b> (nei rapportini) o registri nuove <b>bolle</b>.
                </div>
              </div>
            </div>
          </div>

          {/* Cost breakdown */}
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="bg-white border border-slate-200 p-5 lg:col-span-2">
              <div className="overline mb-3">Ripartizione costi</div>
              <table className="w-full text-sm">
                <tbody>
                  <CostRow label="Costo manodopera" value={econ.costo_manodopera} />
                  {(econ.costo_trasferte || 0) > 0 && (
                    <CostRow label="↳ Trasferte (non tassate)" value={econ.costo_trasferte} muted />
                  )}
                  <CostRow label="Costo materiali (netto)" value={econ.costo_materiali} />
                  <CostRow label="IVA materiali" value={econ.iva_materiali} muted />
                  <CostRow label="Costi sicurezza" value={econ.costo_sicurezza} />
                  <CostRow label="Costi tecnici" value={econ.costo_tecnici} />
                  <CostRow label="Spese generali" value={econ.spese_generali} />
                  <CostRow label="Costi amministrativi" value={econ.costo_amministrativi} />
                  <CostRow label="Costi vari" value={econ.costo_vari} />
                  <tr className="border-t-2 border-slate-900">
                    <td className="py-3 font-display font-bold">COSTO TOTALE</td>
                    <td className="py-3 text-right font-mono font-black">{eur2(econ.costo_totale)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="bg-white border border-slate-200 p-5">
              <div className="overline mb-3">Margini</div>
              <div className="space-y-3">
                <MetricRow label="Guadagno lordo" value={eur(econ.guadagno_lordo)} />
                <MetricRow label="Guadagno netto" value={econ.incidenza_disponibile ? eur(econ.guadagno_netto) : "Da configurare"} strong />
                <MetricRow label="Margine di contribuzione" value={eur(econ.margine_contribuzione)} />
                <MetricRow label="Margine lordo %" value={`${econ.margine_lordo_pct}%`} />
                <MetricRow label="Margine netto %" value={econ.incidenza_disponibile ? `${econ.margine_netto_pct}%` : "Da configurare"} strong />
              </div>
            </div>
          </div>

          {/* Charts */}
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="bg-white border border-slate-200 p-5">
              <div className="overline mb-3">Costi per categoria (bolle)</div>
              <div className="w-full h-64">
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={Object.entries(econ.costi_per_categoria || {}).map(([k, v]) => ({ name: k, value: v }))}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      label
                    >
                      {Object.keys(econ.costi_per_categoria || {}).map((cat, i) => (
                        <Cell key={cat} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v) => eur(v)} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="bg-white border border-slate-200 p-5">
              <div className="overline mb-3">Andamento mensile costi</div>
              <div className="w-full h-64">
                <ResponsiveContainer>
                  <BarChart data={econ.andamento_mensile}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="mese" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
                    <Tooltip formatter={(v) => eur(v)} />
                    <Legend />
                    <Bar dataKey="manodopera" stackId="a" fill="#ea580c" />
                    <Bar dataKey="materiali" stackId="a" fill="#0f172a" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === "crono" && <GanttView cantiere={cantiere} attivita={attivita} />}

      {tab === "notifiche-log" && admin && <NotificheLogTab cantiereId={id} />}

      {tab === "rapportini" && (
        <div className="bg-white border border-slate-200">
          <div className="p-4 border-b border-slate-200 flex items-center justify-between">
            <div><div className="overline">Rapportini</div><h3 className="font-display font-bold">{rapportini.length} rapportini</h3></div>
            <Link to="/rapportini" className="text-sm px-3 py-2 border border-slate-300 hover:bg-slate-50 rounded-sm">Vai a Rapportini</Link>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr><th className="text-left p-3">Data</th><th className="text-left p-3">Responsabile</th><th className="text-left p-3">Lavoratori</th><th className="text-right p-3">Ore ord.</th><th className="text-right p-3">Ore str.</th></tr>
              </thead>
              <tbody>
                {rapportini.map(r => {
                  const ord = (r.lavoratori||[]).reduce((s,l)=>s+parseFloat(l.ore_ordinarie||0),0);
                  const str = (r.lavoratori||[]).reduce((s,l)=>s+parseFloat(l.ore_straordinarie||0),0);
                  return (
                    <tr key={r.rapportino_id} className="border-t border-slate-100">
                      <td className="p-3 font-mono">{r.data}</td>
                      <td className="p-3">{r.responsabile}</td>
                      <td className="p-3 text-xs text-slate-600">{(r.lavoratori||[]).map(l=>l.lavoratore_nome).join(", ")}</td>
                      <td className="p-3 text-right font-mono">{ord}</td>
                      <td className="p-3 text-right font-mono">{str}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "bolle" && (
        <div className="bg-white border border-slate-200">
          <div className="p-4 border-b border-slate-200 flex items-center justify-between gap-3">
            <div>
              <div className="overline">Bolle materiali</div>
              <h3 className="font-display font-bold">{bolle.length} bolle</h3>
            </div>
            <button
              onClick={openNewBolla}
              data-testid="btn-nuova-bolla-cantiere"
              className="inline-flex items-center gap-2 px-3 py-2 bg-orange-600 hover:bg-orange-700 text-white text-sm font-semibold rounded-sm shadow-brutal"
            >
              <Plus className="w-4 h-4" /> Nuova bolla
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left p-3">Data</th><th className="text-left p-3">N°</th><th className="text-left p-3">Fornitore</th>
                  <th className="text-left p-3">Materiale</th><th className="text-right p-3">Q.tà</th><th className="text-right p-3">P.U.</th>
                  <th className="text-right p-3">Imponibile</th><th className="text-right p-3">IVA</th><th className="text-right p-3">Totale</th>
                </tr>
              </thead>
              <tbody>
                {bolle.length === 0 && (
                  <tr><td colSpan={9} className="p-6 text-center text-slate-400 text-sm">
                    Nessuna bolla registrata. Clicca <b>Nuova bolla</b> per aggiungerne una.
                  </td></tr>
                )}
                {bolle.map(b => (
                  <tr key={b.bolla_id} className="border-t border-slate-100">
                    <td className="p-3 font-mono">{b.data}</td>
                    <td className="p-3 font-mono text-xs">{b.numero_bolla}</td>
                    <td className="p-3">{b.fornitore}</td>
                    <td className="p-3">{b.materiale}</td>
                    <td className="p-3 text-right font-mono">{b.quantita} {b.unita_misura}</td>
                    <td className="p-3 text-right font-mono">{eur2(b.prezzo_unitario)}</td>
                    <td className="p-3 text-right font-mono">{eur2(b.imponibile)}</td>
                    <td className="p-3 text-right font-mono text-slate-500">{eur2(b.importo_iva)}</td>
                    <td className="p-3 text-right font-mono font-bold">{eur2(b.totale_con_iva)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "richieste-materiali" && <RichiesteMaterialiTab cantiere={cantiere} />}

      {bollaOpen && (
        <BollaForm
          editingId={bollaEditingId}
          form={bollaForm}
          setForm={setBollaForm}
          cantieri={cantiere ? [cantiere] : []}
          anteprima={bollaAnteprima}
          codiceBolla={codiceBolla}
          onClose={() => { setBollaOpen(false); setBollaEditingId(null); }}
          onSubmit={(f) => saveBolla.mutate(f)}
          saving={saveBolla.isPending}
          handleFiles={handleBollaFiles}
          removeAllegato={removeBollaAllegato}
        />
      )}

      {tab === "presenze" && (
        <div className="bg-white border border-slate-200">
          <div className="p-4 border-b border-slate-200"><div className="overline">Presenze</div><h3 className="font-display font-bold">{presenze.length} timbrature</h3></div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left p-3">Data/ora</th><th className="text-left p-3">Lavoratore</th>
                  <th className="text-left p-3">Tipo</th><th className="text-right p-3">Distanza</th><th className="text-left p-3">Posizione GPS</th><th className="text-left p-3">Stato</th>
                </tr>
              </thead>
              <tbody>
                {presenze.map(p => (
                  <tr key={p.timbratura_id} className="border-t border-slate-100">
                    <td className="p-3 font-mono text-xs">{new Date(p.timestamp).toLocaleString("it-IT")}</td>
                    <td className="p-3">{p.lavoratore_nome}</td>
                    <td className="p-3"><span className="text-[10px] uppercase font-bold px-2 py-0.5 bg-slate-100 border border-slate-200 rounded-sm">{p.tipo.replace('_',' ')}</span></td>
                    <td className="p-3 text-right font-mono">{p.distanza_metri ? `${Math.round(p.distanza_metri)} m` : "-"}</td>
<td className={`p-3 text-xs font-mono ${p.fuori_raggio ? "text-red-700 font-bold" : ""}`}>
{p.lat != null && p.lng != null ? `${p.lat}, ${p.lng}` : "GPS non disponibile"}
</td>
                    <td className="p-3">
                      {p.fuori_raggio ? (
                        <span className="text-[10px] uppercase font-bold px-2 py-0.5 bg-red-100 text-red-800 border border-red-200 rounded-sm">Fuori raggio</span>
                      ) : (
                        <span className="text-[10px] uppercase font-bold px-2 py-0.5 bg-emerald-100 text-emerald-800 border border-emerald-200 rounded-sm">OK</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "documenti" && <DocumentiTab cantiereId={id} />}
      {tab === "fotografie" && <FotografieTab cantiereId={id} />}
      {tab === "comunicazioni" && <ComunicazioniTab cantiereId={id} />}
      {tab === "note" && <NoteCantiereTab cantiereId={id} />}
      {tab === "richieste-subappaltatori" && admin && (
        <RichiesteSubappaltatoriTab cantiereId={id} onOpenSubappalti={() => setTab("subappalti")} />
      )}
      {tab === "subappalti" && admin && (
        <SubappaltiTab cantiereId={id} attivitaOptions={attivita || []} />
      )}
      {tab === "fatture" && admin && (
        <FatturatoEffettivoTab cantiereId={id} attivitaOptions={attivita || []} cantiere={cantiere} />
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between border-b border-slate-100 py-1.5">
      <dt className="text-slate-500">{label}</dt>
      <dd className="font-medium text-slate-900">{value || "-"}</dd>
    </div>
  );
}
function Card({ label, value, sub, highlight, negative }) {
  return (
    <div className={`bg-white border p-4 ${highlight ? "border-orange-500 shadow-brutal" : negative ? "border-red-400" : "border-slate-200"}`}>
      <div className="overline">{label}</div>
      <div className={`font-display font-black text-xl mt-1 ${negative ? "text-red-700" : "text-slate-900"}`}>{value}</div>
      {sub && <div className="text-[10px] text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}
function CostRow({ label, value, muted }) {
  return (
    <tr className="border-b border-slate-100">
      <td className={`py-2 ${muted ? "text-slate-500 text-xs" : ""}`}>{label}</td>
      <td className={`py-2 text-right font-mono ${muted ? "text-slate-500 text-xs" : ""}`}>{eur2(value)}</td>
    </tr>
  );
}
function MetricRow({ label, value, strong }) {
  return (
    <div className="flex justify-between items-center pb-2 border-b border-slate-100 last:border-0">
      <div className="text-sm text-slate-600">{label}</div>
      <div className={`font-mono ${strong ? "font-display font-black text-lg" : "font-semibold"}`}>{value}</div>
    </div>
  );
}
